// OverviewTab.tsx - Final Corrected Version
import React from "react";
import {
  RevenueKPIs,
  DailyAnalytics,
  RevenueTransaction,
} from "../../../services/revenueService";
import { ColorTheme } from "../types/revenue.types";
import { KPICard } from "../shared/KPICard";
import { SimpleLineChart } from "../charts/SimpleLineChart";
import { TransactionCard } from "../cards/TransactionCard";
import { useAuth } from "../../../hooks/useAuth";

interface OverviewTabProps {
  kpis: RevenueKPIs | null;
  dailyData: DailyAnalytics[];
  transactions: RevenueTransaction[];
  colorTheme: ColorTheme;
  userRole: string;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  kpis,
  dailyData,
  transactions,
  colorTheme,
  userRole,
}) => {
  const { isDoctor } = useAuth();

  if (!kpis) {
    return <div>Loading...</div>;
  }

  // Backend already filters transactions for doctors
  // For doctors, only show provider revenue in daily chart
  const filteredDailyData = isDoctor()
    ? dailyData.map((day) => ({
        ...day,
        // Replace total_revenue with provider_revenue for doctors
        total_revenue: day.provider_revenue || 0,
      }))
    : dailyData;

  return (
    <div>
      {/* KPI Cards - Different for Doctors vs Admins */}
      {isDoctor() ? (
        // DOCTOR VIEW: Only show their earnings
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <KPICard
            icon="💰"
            title="My Earnings"
            value={kpis.current_period.provider_revenue}
            subtitle={`From ${kpis.current_period.total_transactions} services`}
            colorTheme={colorTheme}
            trend={{
              value: kpis.growth_metrics.revenue_growth_percentage,
              isPositive: kpis.growth_metrics.revenue_growth_percentage > 0,
            }}
          />
          <KPICard
            icon="📊"
            title="Average Per Service"
            value={
              kpis.current_period.total_transactions > 0
                ? kpis.current_period.provider_revenue /
                  kpis.current_period.total_transactions
                : 0
            }
            colorTheme={colorTheme}
          />
          <KPICard
            icon="🔢"
            title="Total Services"
            value={kpis.current_period.total_transactions}
            subtitle="Services provided"
            colorTheme={colorTheme}
          />
          <KPICard
            icon="📈"
            title="Growth"
            value={`${kpis.growth_metrics.revenue_growth_percentage > 0 ? "+" : ""}${kpis.growth_metrics.revenue_growth_percentage.toFixed(1)}%`}
            subtitle="vs previous period"
            colorTheme={colorTheme}
            trend={{
              value: kpis.growth_metrics.revenue_growth_percentage,
              isPositive: kpis.growth_metrics.revenue_growth_percentage > 0,
            }}
          />
        </div>
      ) : (
        // ADMIN VIEW: Show all revenue breakdown
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <KPICard
            icon="💰"
            title="Total Revenue"
            value={kpis.current_period.total_revenue}
            subtitle={`${kpis.current_period.total_transactions} transactions`}
            colorTheme={colorTheme}
            trend={{
              value: kpis.growth_metrics.revenue_growth_percentage,
              isPositive: kpis.growth_metrics.revenue_growth_percentage > 0,
            }}
          />
          <KPICard
            icon="🏥"
            title="Clinic Revenue"
            value={kpis.current_period.clinic_revenue}
            colorTheme={colorTheme}
          />
          <KPICard
            icon="👨‍⚕️"
            title="Provider Revenue"
            value={kpis.current_period.provider_revenue}
            colorTheme={colorTheme}
          />
          <KPICard
            icon="❤️"
            title="Charity"
            value={kpis.current_period.charity_revenue}
            colorTheme={colorTheme}
          />
        </div>
      )}

      {/* Daily Revenue Chart */}
      <div
        style={{
          background: "white",
          padding: "24px",
          borderRadius: "12px",
          marginBottom: "24px",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: colorTheme.gray900,
            marginBottom: "16px",
          }}
        >
          {isDoctor() ? "My Daily Earnings" : "Daily Revenue Trend"}
        </h3>
        <SimpleLineChart
          data={filteredDailyData}
          height={250}
          color={colorTheme.primary}
        />
      </div>

      {/* Recent Transactions */}
      <div
        style={{ background: "white", padding: "24px", borderRadius: "12px" }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: colorTheme.gray900,
            marginBottom: "16px",
          }}
        >
          {isDoctor() ? "My Recent Services" : "Recent Transactions"}
        </h3>
        {transactions.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: colorTheme.gray500,
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📊</div>
            <p>
              {isDoctor()
                ? "No services found for the selected period"
                : "No transactions found for the selected period"}
            </p>
          </div>
        ) : (
          <div style={{ maxHeight: "600px", overflowY: "auto" }}>
            {transactions.slice(0, 10).map((transaction) => (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                colorTheme={colorTheme}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
